import { motion } from "framer-motion";
import { Tractor, HardHat, MonitorSmartphone, Stethoscope, ShieldCheck } from "lucide-react";

const CATEGORIES = [
  {
    id: "agricola",
    name: "Maquinaria Agrícola",
    tag: "Tractores · Cosechadoras · Fumigadoras",
    desc:
      "Cubre daños físicos de la maquinaria y la responsabilidad civil por daños corporales y/o materiales causados a terceros por el equipo.",
    Icon: Tractor,
    image: "/generated/cat-agricola.jpg",
    rc: true,
  },
  {
    id: "construccion",
    name: "Construcción y Minería",
    tag: "Excavadoras · Grúas · Cargadores · Camiones mineros",
    desc:
      "Protege el equipo pesado en obra y faena minera: daños físicos y responsabilidad civil frente a terceros.",
    Icon: HardHat,
    image: "/generated/cat-construccion.jpg",
    rc: true,
  },
  {
    id: "medicas",
    name: "Máquinas Médicas",
    tag: "MRI · Tomógrafos · Ecógrafos · Monitores",
    desc:
      "Cubre equipamiento médico de clínicas y hospitales contra daños físicos: resonadores, tomógrafos, ecógrafos, monitores y otros.",
    Icon: Stethoscope,
    image: "/generated/cat-medicas.jpg",
    rc: false,
  },
  {
    id: "electronicos",
    name: "Equipos Electrónicos",
    tag: "PC · Celulares · Comunicaciones · Medición",
    desc:
      "Equipos electrónicos de uso particular o comercial contra daños físicos: computación, celulares, comunicaciones y medición.",
    Icon: MonitorSmartphone,
    image: "/generated/cat-electronicos.jpg",
    rc: false,
  },
];

const CategoryCard = ({ category, index }) => {
  const Icon = category.Icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`machine-card-${category.id}`}
      className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-brand-ink/10"
    >
      {/* Imagen de la categoría */}
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={category.image}
          alt={`Seguro de ${category.name}`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/80 via-brand-ink/15 to-transparent" />
        <div className="absolute top-3 left-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-yellow text-brand-ink shadow-md">
          <Icon className="h-5 w-5" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-white/85">
            {category.tag}
          </p>
          <h3 className="font-display mt-1 text-lg sm:text-xl font-bold text-white leading-tight">
            {category.name}
          </h3>
        </div>
      </div>

      {/* Texto + coberturas */}
      <div className="flex flex-1 flex-col p-5">
        <p className="text-sm leading-relaxed text-gray-700">{category.desc}</p>
        <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-brand-ink">
          <ShieldCheck className="h-4 w-4 text-brand-yellow" />
          Daños físicos {category.rc ? "+ Responsabilidad Civil" : ""}
        </div>
      </div>
    </motion.div>
  );
};

export const MachineShowcase = () => {
  return (
    <section id="maquinas" className="py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-yellow">
            Coberturas disponibles
          </p>
          <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-brand-ink">
            Aseguramos tu maquinaria y equipos.
          </h2>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-gray-600">
            Desde el tractor del campo hasta la excavadora en obra, el tomógrafo de la
            clínica y el laptop de la oficina: cubrimos daños físicos y responsabilidad
            civil con el respaldo de las principales aseguradoras de Chile.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CATEGORIES.map((cat, i) => (
            <CategoryCard key={cat.id} category={cat} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
